import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Tag,
  Tooltip,
  Input,
} from "antd";
import { useCallback, useEffect, useState, useMemo } from "react";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import { ItInventoryModalAdd } from "./ItInventoryModalAdd"; // Assuming this is the modal name
import { ErrorToast, SuccessToast } from "../../ToastComponent";
import { ItInventoryUpdateModal } from "./ItInventoryUpdateModal";

const { Option } = Select;

// Asset Type Options from requirements
const ASSET_TYPES = ["Laptop", "Desktop"];

// Helper for date formatting
const formatDate = (date) => {
  if (!date) return "N/A";
  const d = new Date(date);
  return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString("en-GB"); // DD/MM/YYYY
};

export const ItInventory = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState();
  const [searchFilters, setSearchFilters] = useState({
    branch: "",
    assetType: null,
    searchTerm: "", // New: specific for Serial/SAP search
  });

  const fetchBranches = async () => {
    try {
      const response = await axios.get(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      const branchNames = response?.data?.data?.map((b) => b.name) || [];
      setBranches([...new Set(branchNames)]);
    } catch (error) {
      console.error("Failed to load branches:", error);
    }
  };

  const fetchItInventoryData = async () => {
    try {
      setLoading(true);
      // Fetching from the new IT Inventory endpoint
      const response = await axios.get(
        `${Api_Url}api/v1/ItInventory?PageNumber=1&PageSize=500`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      setData(response?.data?.data || []);
      console.log(data)
    } catch (error) {
      ErrorToast("Failed to load IT inventory data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchItInventoryData();
  }, []);

  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (searchFilters.branch) {
      filtered = filtered.filter(
        (item) =>
          item.branchName?.toLowerCase() === searchFilters.branch.toLowerCase()
      );
    }
    if (searchFilters.assetType) {
      filtered = filtered.filter(
        (item) => item.assetType === searchFilters.assetType
      );
    }
    if (searchFilters.searchTerm) {
      const term = searchFilters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.sapCode?.toLowerCase().includes(term) ||
          item.serialNumber?.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [data, searchFilters]);

  const handleFilterChange = (key, value) => {
    setSearchFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setSearchFilters({ branch: "", assetType: null, searchTerm: "" });
  };

  const handleExportToCsv = async () => {
    try {
      const response = await axios.get(`${Api_Url}api/v1/ItInventory/export`, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user"))?.accessToken
          }`,
        },
      });
      const fileURL = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute(
        "download",
        `IT_Inventory_Report_${new Date().getTime()}.csv`
      );
      document.body.appendChild(link);
      link.click();
      SuccessToast("Export successful!");
    } catch (error) {
      ErrorToast("Failed to export inventory data");
    }
  };

  const columns = [
    {
      title: "Laptop UUID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Asset Type",
      dataIndex: "assetType",
      render: (text) => <Tag color="blue">{text}</Tag>,
      width: 100,
    },
    {
      title: "SAP Code",
      dataIndex: "sapCode",
      sorter: (a, b) => a.sapCode.localeCompare(b.sapCode),
    },
    {
      title: "Make/Model",
      dataIndex: "makeModel",
    },
    {
      title: "Serial Number",
      dataIndex: "serialNumber",
    },
    {
      title: "Specification",
      dataIndex: "description",
    },
    {
      title: "Branch",
      dataIndex: "branchName",
    },
    {
      title: "Assigned To",
      dataIndex: "employeeName",
      render: (name, record) => (
        <Tooltip title={`Code: ${record.employeeCode}`}>
          {`${name} (${record.employeeCode})` || "Unassigned"}
        </Tooltip>
      ),
    },
    {
      title: "Issue Date",
      dataIndex: "issueDate",
      render: (date) => formatDate(date),
      width: 110,
    },
    {
      title: "Return Date",
      dataIndex: "returnDate",
      render: (date) => formatDate(date),
      width: 110,
    },
    {
      title: "Previously Assigned To",
      dataIndex: "previouslyAssigned",
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
    },
    {
      title: "Re-Assigned Id",
      dataIndex: "reassignedId",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Scrap",
      dataIndex: "scrapStatus",
      render: (scrap) => (
        <Tag color={scrap === "Active" ? "volcano" : "cyan"}>{scrap}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          color="default"
          disabled={record.status === "Inactive" && record.scrapStatus === "Active"}
          onClick={() => setSelectedAsset(record)}
        >
          Update Status
        </Button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">IT Inventory Management</h3>
            <div className="d-flex align-items-center gap-2">
              <button onClick={handleExportToCsv} className="btn btn-success">
                <i className="fa-solid fa-file-excel me-2"></i>Export CSV
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setIsModalOpen(true)}
              >
                Add New Asset
              </button>
            </div>
          </div>
        </div>

        <Card className="mb-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Search Serial / SAP Code"
                value={searchFilters.searchTerm}
                onChange={(e) =>
                  handleFilterChange("searchTerm", e.target.value)
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Branch"
                value={searchFilters.branch || undefined}
                onChange={(value) => handleFilterChange("branch", value)}
                allowClear
                style={{ width: "100%" }}
                showSearch
              >
                {branches.map((branch) => (
                  <Option key={branch} value={branch}>
                    {branch}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Asset Type"
                value={searchFilters.assetType}
                onChange={(value) => handleFilterChange("assetType", value)}
                allowClear
                style={{ width: "100%" }}
              >
                {ASSET_TYPES.map((type) => (
                  <Option key={type} value={type}>
                    {type}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Button onClick={clearFilters} block>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>

        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record.id}
            loading={loading}
            pagination={{ showSizeChanger: true }}
            scroll={{ x: 1200 }}
          />
        </div>

        {isModalOpen && (
          <ItInventoryModalAdd
            onClose={() => setIsModalOpen(false)}
            onSuccess={fetchItInventoryData}
          />
        )}
        {selectedAsset && (
          <ItInventoryUpdateModal
            asset={selectedAsset}
            onClose={() => setSelectedAsset(null)}
            onSuccess={fetchItInventoryData}
          />
        )}
      </div>
    </div>
  );
};
